import { FC, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useCloudflareStorage } from '@/hooks/useCloudflareStorage';
import { ALLOWED_CONTENT_TYPES } from '@/lib/cloudflare';
import { toast } from '@/components/ui/use-toast';
import { FileUpload } from '@/components/ui/file-upload';
import type { LessonFormData } from './types';

interface MediaUploadFieldsProps {
  form: UseFormReturn<LessonFormData>;
}

export const MediaUploadFields: FC<MediaUploadFieldsProps> = ({ form }) => {
  const { uploadFile } = useCloudflareStorage();
  const [uploadingField, setUploadingField] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileUpload = async (file: File, field: keyof LessonFormData) => {
    let progressInterval: NodeJS.Timeout | null = null;

    try {
      // Validar tipo de archivo
      const type = field === 'video_url' ? 'VIDEO' : 'AUDIO';
      const allowedTypes = ALLOWED_CONTENT_TYPES[type].types;
      const maxSize = ALLOWED_CONTENT_TYPES[type].maxSize;

      if (!allowedTypes.includes(file.type)) {
        throw new Error(`Tipo de archivo no permitido. Use: ${allowedTypes.join(', ')}`);
      }

      if (file.size > maxSize) {
        throw new Error(`El archivo es demasiado grande. Máximo: ${Math.round(maxSize / (1024 * 1024))}MB`);
      }

      setUploadingField(field);
      setUploadProgress(0);

      // Simular progreso mientras se sube
      progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 500);

      const fileUrl = await uploadFile(file, type);
      
      // Actualizar el formulario con la URL
      form.setValue(field, fileUrl);
      
      toast({
        title: "Archivo subido",
        description: "El archivo se ha subido correctamente",
      });

    } catch (error: any) {
      console.error('Error uploading file:', error);
      toast({
        title: "Error al subir archivo",
        description: error.message || "Hubo un problema al subir el archivo",
        variant: "destructive",
      });
      form.setValue(field, '');
    } finally {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      setUploadProgress(0);
      setUploadingField(null);
    }
  };

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="video_url"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Video de la Lección</FormLabel>
            <FileUpload
              value={field.value}
              onChange={(file) => handleFileUpload(file, 'video_url')}
              accept={ALLOWED_CONTENT_TYPES.VIDEO.types.join(',')}
              maxSize={ALLOWED_CONTENT_TYPES.VIDEO.maxSize}
              loading={uploadingField === 'video_url'}
              progress={uploadingField === 'video_url' ? uploadProgress : 0}
              onClear={() => form.setValue('video_url', '')}
            />
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="audio_url"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Audio de la Lección</FormLabel>
            <FileUpload
              value={field.value}
              onChange={(file) => handleFileUpload(file, 'audio_url')}
              accept={ALLOWED_CONTENT_TYPES.AUDIO.types.join(',')}
              maxSize={ALLOWED_CONTENT_TYPES.AUDIO.maxSize}
              loading={uploadingField === 'audio_url'}
              progress={uploadingField === 'audio_url' ? uploadProgress : 0}
              onClear={() => form.setValue('audio_url', '')}
            />
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};